import { endOfDay, startOfDay } from "date-fns";
import { useState } from "react";
import { z } from "zod";

import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { TimePeriod, TimeRange } from "@smart/itops-sb-design-system-dom";

import { toDateTimeRange } from "../utils/time-filters";

const convertToRange = (filter: TimePeriod, range?: TimeRange) => {
  switch (filter) {
    case "allTime":
      return undefined;
    case "customDates":
      if (!range?.from || !range?.to) {
        return undefined;
      }
      return {
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      };
    default:
      return toDateTimeRange(filter, loadLocale().country);
  }
};

export const useTimeFilterValue = ({
  filterStorageKey,
}: {
  filterStorageKey: string;
}) => {
  const [storedTimeFilter] = useStorage<TimePeriod>({
    defaultValue: "thisWeek",
    key: filterStorageKey,
    schema: z.enum([
      "allTime",
      "today",
      "thisWeek",
      "lastWeek",
      "thisMonth",
      "lastMonth",
      "customDates",
    ]),
    storage: "local",
  });
  const [storedTimeRangeFilter] = useStorage<{
    from?: Date;
    to?: Date;
  }>({
    defaultValue: { from: undefined, to: undefined },
    key: `${filterStorageKey}-range`,
    schema: z.object({
      from: z.coerce.date(),
      to: z.coerce.date(),
    }),
    storage: "local",
  });
  const [filterValue, setFilterValue] = useState<TimeRange | undefined>(
    convertToRange(storedTimeFilter, storedTimeRangeFilter),
  );

  return {
    timeFilter: filterValue,
    setTimeFilter: (filter: TimePeriod, range?: TimeRange) => {
      const filterRange = convertToRange(filter, range);
      if (
        filterRange?.from?.toISOString() !== filterValue?.from?.toISOString() ||
        filterRange?.to?.toISOString() !== filterValue?.to?.toISOString()
      ) {
        setFilterValue(filterRange);
      }
    },
  };
};
